import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { RowSelectedEvent } from '@ag-grid-community/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { AiSettingsGet, GetAiProviderModelsParams } from '@app/services/ai-provider.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-ai-settings-models',
    templateUrl: './ai-settings-models.component.html',
    styleUrls: ['./ai-settings-models.component.css'],
    standalone: false,
})
export class AiSettingsModelsComponent implements OnChanges, OnDestroy {
    @Input()
    public page!: AiSettingsGet;

    @Input()
    public active = false;

    public providersGrid: GridOptions<AiProviderGridRow> = new Grid<AiProviderGridRow>().setRememberId('w_aisettings.providers_grid').setLoadDataFunction({ method: 4080 });

    public modelsGrid?: GridOptions;

    public showProviderSelectionPrompt = false;

    private readonly destroy$ = new Subject<void>();

    private readonly selectedProvider$ = new Subject<string | number | null | undefined>();

    constructor() {
        this.selectedProvider$.pipe(debounceTime(50), takeUntil(this.destroy$)).subscribe((providerKeyno) => {
            if (providerKeyno === undefined) return;

            this.page.ai_provider_keyno = providerKeyno;
            this.showProviderSelectionPrompt = providerKeyno === null;

            if (providerKeyno === null) {
                this.modelsGrid = undefined;
                return;
            }

            this.readModelsGrid();
        });
    }

    public onProvidersGridRead(): void {
        if (!this.active) return;

        const gridfunc = this.providersGrid.gridfunc;

        if ((gridfunc?.getRows().length ?? 0) === 0) {
            this.selectedProvider$.next(null);
            return;
        }

        const selectedProvider = gridfunc?.getSelectedRows()[0];
        if (selectedProvider) {
            this.selectedProvider$.next(selectedProvider.ai_provider_keyno);
        } else {
            gridfunc?.selectRow(0);
        }
    }

    public onProviderSelected(event: RowSelectedEvent<AiProviderGridRow>): void {
        if (!this.active) return;

        const provider = event.node.isSelected() ? event.data : event.api.getSelectedRows()[0];
        this.selectedProvider$.next(provider?.ai_provider_keyno ?? null);
    }

    private readModelsGrid(): void {
        if (!this.modelsGrid) {
            this.modelsGrid = new Grid().setRememberId('w_aisettings.models_grid').setLoadDataFunction({ method: 4040, parameters: () => this.getModelsGridParams() });
        } else {
            this.modelsGrid.gridfunc?.read(false);
        }
    }

    private getModelsGridParams(): GetAiProviderModelsParams {
        return { ai_provider_keyno: this.page.ai_provider_keyno };
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['page']?.currentValue) {
            this.page.ai_provider_keyno = null;
            this.modelsGrid = undefined;
            this.showProviderSelectionPrompt = false;
            // Cancel queued selection while the providers grid is loading.
            this.selectedProvider$.next(undefined);
        }

        if (changes['active']) {
            if (!this.active) {
                this.selectedProvider$.next(undefined);
            } else if (!changes['active'].firstChange) {
                this.showProviderSelectionPrompt = false;
                this.providersGrid.gridfunc?.read(false);
            }
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

interface AiProviderGridRow extends GridRow {
    ai_provider_keyno: string | number;
}
